import React, { PropsWithChildren, useEffect } from 'react';

import { useTrans } from '@/custom-hooks/useTrans';
import { TypeApplicationStore } from '@stores/application-store';
import { TypeProfileStore } from '@stores/profile-store';
import { injectStores } from '@stores/factories/store-utils';

import {
    HEADER_TOOLS,
    LOGIN_TOOLS,
    TARGET_BACKEND,
    LOCAL_SERVER_REPLACE_LOOKUP,
} from '@/constants';

import { serializeObjectForUrl } from '@utils/utils';

import { PageLayout, PageLayoutFull } from '@components/page-layout/page-layout';
import BannerV2 from '@/components/common/legos/banner/banner-v2';
import SwitchLink from '@components/menus/menu/switch-link/switch-link';

const { SIGN_UP, RESET_PASSWORD, SIGN_IN } = LOGIN_TOOLS;

function dictBaseSelector() {
    const currentPath = window.location.href;

    if (currentPath.indexOf('/project-invitation') !== -1)
        return 'common:ui-errors.logged-out-invitation-page';
    if (currentPath.indexOf('/access-denied') !== -1) return 'common:ui-errors.access-denied';
    if (currentPath.indexOf('/restricted-access') !== -1) return 'common:ui-errors.restricted';

    return 'common:ui-errors.logged-out-protected-page';
}

type LoggedOutOrProtectedPageProps = {
    urlData: {
        formMode: keyof typeof HEADER_TOOLS;
        language: string;
        loginBaseUrl: string;
        loginClientId: string;
    };
};
function LoggedOutOrProtectedPage(props: LoggedOutOrProtectedPageProps) {
    const { t } = useTrans();
    const { urlData } = props;
    const { language, loginBaseUrl, loginClientId } = urlData;

    const dictionaryBase = dictBaseSelector();

    return (
        <PageLayout>
            <PageLayoutFull subgrid>
                <BannerV2
                    id='LOGGED_OUT'
                    dictionaryBase={dictionaryBase}
                    imageId='logged_out'
                    lightText={false}
                    minHeight={800}
                    ctasComponent={
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {t(`${dictionaryBase}.sign-in`, {
                                AK_LINK_SIGN_IN: (
                                    <SwitchLink
                                        akTestId='LoggedOut|ProtectedPage|SIGN_IN'
                                        to={buildLoginUrl(
                                            SIGN_IN,
                                            language,
                                            loginBaseUrl,
                                            loginClientId
                                        )}
                                        forceSameTab
                                        includeLanguageSlug={false}
                                    />
                                ),
                                AK_LINK_SIGN_UP: (
                                    <SwitchLink
                                        akTestId='LoggedOut|ProtectedPage|SIGN_UP'
                                        to={buildLoginUrl(
                                            SIGN_UP,
                                            language,
                                            loginBaseUrl,
                                            loginClientId
                                        )}
                                        forceSameTab
                                        includeLanguageSlug={false}
                                    />
                                ),
                            })}
                        </span>
                    }
                />
            </PageLayoutFull>
        </PageLayout>
    );
}

const cognitoMode = {
    [SIGN_IN]: 'login/continue',
    [SIGN_UP]: 'signup',
    [RESET_PASSWORD]: 'forgotPassword',
} as const;

export const buildLoginUrl = (
    formMode: keyof typeof cognitoMode,
    language: string,
    loginBaseUrl: string,
    loginClientId: string
) => {
    const hostName = window.location.hostname;
    const pathSearchHash = window.location.href.replace(window.location.origin, '');
    const mode = cognitoMode[formMode]!;
    const redirect_uri =
        hostName === 'localhost.gowwise.com'
            ? `${LOCAL_SERVER_REPLACE_LOOKUP[TARGET_BACKEND]}/cognito/`
            : `https://${hostName}/cognito/`;

    const searchObject = {
        lang: language,
        client_id: loginClientId,
        response_type: 'code',
        scope: 'email+openid+profile',
        redirect_uri: encodeURIComponent(redirect_uri),
        state: encodeURIComponent(pathSearchHash),
    };

    return `${loginBaseUrl}/${mode}?${serializeObjectForUrl(searchObject)}`;
};

type Props = {
    ApplicationStore: TypeApplicationStore;
    ProfileStore: TypeProfileStore;
    initFormMode?: keyof typeof HEADER_TOOLS;
    inLine?: boolean;
};
function SignInSignUp(props: Props) {
    const {
        ApplicationStore: {
            userLanguage,
            getLoginClientInfo,
            headerTools: { openedHeaderTool },
        },
        ProfileStore: { userIsLoggedIn },
        initFormMode = SIGN_IN,
        inLine,
    } = props;

    const { loginBaseUrl, loginClientId } = getLoginClientInfo();

    const signTool = openedHeaderTool || initFormMode;
    useEffect(() => {
        if (!inLine) {
            window.location.assign(
                buildLoginUrl(signTool, userLanguage, loginBaseUrl, loginClientId)
            );
        }
    }, [inLine]);

    return inLine && !userIsLoggedIn ? (
        <LoggedOutOrProtectedPage
            urlData={{ initFormMode: signTool, userLanguage, loginBaseUrl, loginClientId }}
        />
    ) : null;
}

export default injectStores(['ApplicationStore', 'ProfileStore'])(SignInSignUp);

type TriggerCognitoProps = {
    ApplicationStore: TypeApplicationStore;
    targetTool: string;
} & PropsWithChildren<{}>;
function WrappedTriggerCognito(props: TriggerCognitoProps) {
    const {
        ApplicationStore: { showToolInHeader },
        children,
        targetTool,
    } = props;
    return (
        <span
            className='ak-link'
            onClick={(e) => {
                showToolInHeader(targetTool);
                e.stopPropagation();
            }}
        >
            {children}
        </span>
    );
}
export const TriggerCognito = injectStores(['ApplicationStore'])(WrappedTriggerCognito);
